import { FilterSchema, filters } from '@adc/blueprint'
import { Maybe } from '@adc/sdk/types'
import {
  DatatableColumnDefinition,
  BuildDSLParams,
  BuildDSLFunction,
} from '../types'
import _ from 'lodash'

export const escapeOpenSearchQuery = (str: string) => {
  if (!str) return ''

  // List of reserved characters:
  // + - = && || > < ! ( ) { } [ ] ^ " ~ * ? : \ /
  // Note: We escape \ first so we don't accidentally escape our own escape characters.
  const reservedChars = /([\\+\-=&|><!(){}\[\]^"~*?:\/])/g

  return str.replace(reservedChars, '\\$1')
}

export const buildTextSearchDSL = ({
  searchText,
  columns,
}: {
  searchText: string
  columns: DatatableColumnDefinition[]
}) => {
  if (!searchText) {
    return null
  }
  const fields = columns
    .filter(
      (column: DatatableColumnDefinition) =>
        column.colProps?.searchable === true
    )
    .map((column: DatatableColumnDefinition) => column.apiRef)
  return [
    {
      query_string: {
        fields,
        value: `*${escapeOpenSearchQuery(searchText)}*`,
      },
    },
  ]
}

const hasValidRangeValue = (
  minOrMaxValue: Maybe<FilterSchema.RangeControlValueType> | undefined,
  controlValueType: FilterSchema.RangeControlValueType
) => {
  if (controlValueType === 'number') {
    return minOrMaxValue !== null && !isNaN(Number(minOrMaxValue))
  } else if (controlValueType === 'Date') {
    return minOrMaxValue instanceof Date
  }
  return !!minOrMaxValue
}

export const processFilterDefinition = ({
  filterState,
  filterDefinition,
}: {
  filterState: FilterSchema.DefaultFilterState
  filterDefinition: FilterSchema.FilterDefinition
}) => {
  const filterValue = filterState[filterDefinition.name]

  // Early return if filter value is empty
  if (_.isEmpty(filterValue)) {
    return null
  }

  // Use custom DSL clause builder if provided
  if (filterDefinition.buildDSLClause) {
    // eslint-disable-next-line
    return filterDefinition.buildDSLClause(filterValue, filterDefinition)
  }

  // Default DSL generation logic
  let clause = null
  switch (filterDefinition.controlType) {
    case 'multiSelect':
      clause = {
        equals: {
          fields: filterDefinition.name,
          values: filterValue,
        },
      }
      break
    case 'singleSelect':
      if (filterValue === filters.constants.nonExistentFilterOption.value) {
        clause = {
          not: {
            expression: {
              exists: {
                field: filterDefinition.name,
              },
            },
          },
        }
      } else {
        clause = {
          equals: {
            fields: filterDefinition.name,
            values: [filterValue],
          },
        }
      }
      break
    case 'boolean':
      clause = {
        equals: {
          fields: filterDefinition.name,
          values: [filterValue ? 'true' : 'false'],
        },
      }
      break
    case 'range': {
      const value =
        filterValue as FilterSchema.RangeValueType<FilterSchema.RangeControlValueType>

      let max = value.max

      if (max !== null) {
        if (filterDefinition.controlValueType === 'number') {
          max = (max as number) + 1
        } else if (filterDefinition.controlValueType === 'Date') {
          const maxDate = new Date((max as Date).getTime())
          maxDate.setDate(maxDate.getDate() + 1)
          max = maxDate
        }
      }

      if (
        hasValidRangeValue(value.min, filterDefinition.controlValueType) &&
        hasValidRangeValue(value.max, filterDefinition.controlValueType)
      ) {
        clause = {
          range: {
            field: filterDefinition.name,
            gte: value.min,
            lt: max,
          },
        }
      } else if (
        hasValidRangeValue(value.min, filterDefinition.controlValueType)
      ) {
        clause = {
          range: {
            field: filterDefinition.name,
            gte: value.min,
          },
        }
      } else if (
        hasValidRangeValue(value.max, filterDefinition.controlValueType)
      ) {
        clause = {
          range: {
            field: filterDefinition.name,
            lt: max,
          },
        }
      }
      break
    }
    default:
      throw new Error(
        // @ts-ignore next line (inferred never which is fine - but this is just in case)
        `Unsupported filter type: ${filterDefinition.controlType}` // eslint-disable-line
      )
  }
  return clause
}

export const buildFilterDSL = ({
  filterState,
  filterDefinitions,
}: {
  filterState: FilterSchema.DefaultFilterState
  filterDefinitions: FilterSchema.FilterGroupDefinitionArray
}) => {
  const clauses: Record<string, any>[] = []

  if (!filterDefinitions || !filterState) {
    return null
  }

  // loop through filter definitions, find corresponding filter state and then switch on filter type
  filterDefinitions.forEach(filterGroupDefinition => {
    filterGroupDefinition.items.forEach(itemDefinition => {
      if (itemDefinition.itemType === 'section') {
        ;(
          itemDefinition as FilterSchema.FilterSectionDefinition
        ).filters.forEach(filterDefinition => {
          const clause = processFilterDefinition({
            filterState,
            filterDefinition,
          })
          if (clause !== null) {
            clauses.push(clause)
          }
        })
      } else {
        const clause = processFilterDefinition({
          filterState,
          filterDefinition: itemDefinition as FilterSchema.FilterDefinition,
        })
        if (clause !== null) {
          clauses.push(clause)
        }
      }
    })
  })

  return clauses
}

export const buildDSL: BuildDSLFunction = ({
  searchText,
  filterState,
  columns,
  filterDefinitions,
}: BuildDSLParams) => {
  const textSearchClauses = buildTextSearchDSL({ searchText, columns })
  const filterClauses = buildFilterDSL({ filterState, filterDefinitions })
  const allClauses = [...(textSearchClauses || []), ...(filterClauses || [])]

  if (allClauses.length === 0) {
    return { all: {} }
  } else if (allClauses.length === 1) {
    return allClauses[0]
  } else {
    return {
      and: {
        expressions: allClauses,
      },
    }
  }
}
